/**
 * The Client Documents location picker: a compact button naming where you are
 * (All documents, Ungrouped, or a File Group) that opens a searchable list of
 * every location. Replaces the tab strip, which stopped scaling past a handful
 * of groups.
 */
import { useRef, useState } from 'react';
import {
	Box,
	Button,
	CircularProgress,
	InputAdornment,
	ListSubheader,
	MenuItem,
	MenuList,
	Popover,
	TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderOffOutlinedIcon from '@mui/icons-material/FolderOffOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import SearchIcon from '@mui/icons-material/Search';
import { count } from '../../utils/format';
import type { FileGroupRecord } from '../../schemas';
import { selectionKey, type Selection } from './membership';

/** A count, or a small spinner while it is still being worked out. */
function Count({ n }: { n: number | null }) {
	return n === null ? (
		<CircularProgress size={10} thickness={6} aria-label="Counting" />
	) : (
		<>{count(n)}</>
	);
}

export function GroupSelector({
	groups,
	selection,
	allCount,
	ungroupedCount,
	canEdit,
	onSelect,
	onNewGroup,
}: {
	groups: FileGroupRecord[];
	selection: Selection;
	allCount: number | null;
	ungroupedCount: number | null;
	canEdit: boolean;
	onSelect: (next: Selection) => void;
	onNewGroup: () => void;
}) {
	const [anchor, setAnchor] = useState<HTMLElement | null>(null);
	const [query, setQuery] = useState('');
	const menuListRef = useRef<HTMLUListElement>(null);
	const open = Boolean(anchor);
	const current = selectionKey(selection);

	const sorted = [...groups].sort((a, b) => a.name.localeCompare(b.name));
	const needle = query.trim().toLowerCase();
	const matching = needle
		? sorted.filter((group) => group.name.toLowerCase().includes(needle))
		: sorted;

	const currentGroup =
		selection.kind === 'group'
			? (groups.find((group) => group.pkFileGroupId === selection.id) ??
				null)
			: null;
	const name =
		selection.kind === 'all'
			? 'All data files'
			: selection.kind === 'ungrouped'
				? 'Ungrouped'
				: (currentGroup?.name ?? 'Group');
	const currentCount: number | null | undefined =
		selection.kind === 'all'
			? allCount
			: selection.kind === 'ungrouped'
				? ungroupedCount
				: currentGroup?.fileCount;
	const TriggerIcon =
		selection.kind === 'all'
			? Inventory2OutlinedIcon
			: selection.kind === 'ungrouped'
				? FolderOffOutlinedIcon
				: FolderOutlinedIcon;

	const close = () => setAnchor(null);
	const choose = (next: Selection) => {
		close();
		onSelect(next);
	};

	const item = (
		key: string,
		label: string,
		n: number | null,
		next: Selection,
	) => {
		const selected = key === current;
		return (
			<MenuItem
				key={key}
				selected={selected}
				onClick={() => choose(next)}
				sx={{
					gap: 'var(--s2)',
					minHeight: 32,
					fontSize: 'var(--fs-body)',
				}}
			>
				<Box
					component="span"
					sx={{ width: 16, display: 'inline-flex', flex: '0 0 auto' }}
				>
					{selected && (
						<CheckIcon
							sx={{ fontSize: 16, color: 'var(--accent)' }}
							aria-hidden
						/>
					)}
				</Box>
				<Box
					component="span"
					title={label}
					sx={{
						flex: 1,
						minWidth: 0,
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',
						fontWeight: selected ? 600 : 400,
					}}
				>
					{label}
				</Box>
				<Box
					component="span"
					sx={{
						fontSize: 'var(--fs-small)',
						color: 'var(--ink-3)',
						display: 'inline-flex',
						alignItems: 'center',
					}}
				>
					<Count n={n} />
				</Box>
			</MenuItem>
		);
	};

	return (
		<>
			<Button
				variant="text"
				color="inherit"
				aria-haspopup="dialog"
				aria-expanded={open}
				onClick={(event) => {
					setQuery('');
					setAnchor(event.currentTarget);
				}}
				/* Drawn as a field, like the Kind and Status selects beside the
           search, so it reads as a control and not as a heading. */
				sx={{
					height: 'var(--ctl-h)',
					minWidth: 260,
					maxWidth: 440,
					px: 'var(--s3)',
					gap: 'var(--s2)',
					justifyContent: 'flex-start',
					textTransform: 'none',
					fontWeight: 400,
					fontSize: 'var(--fs-body)',
					color: 'var(--ink)',
					bgcolor: 'var(--field)',
					border: '1px solid var(--line-strong)',
					borderRadius: 'var(--r-sm)',
					'&:hover': {
						bgcolor: 'var(--field)',
						borderColor: 'var(--ink-3)',
					},
					...(open && {
						borderColor: 'var(--accent)',
						boxShadow: '0 0 0 3px var(--accent-surface)',
					}),
					'&:focus-visible': {
						outline: 'none',
						borderColor: 'var(--accent)',
						boxShadow: '0 0 0 3px var(--accent-surface)',
					},
				}}
			>
				<TriggerIcon
					sx={{ fontSize: 18, color: 'var(--ink-3)' }}
					aria-hidden
				/>
				<Box
					component="span"
					sx={{ color: 'var(--ink-3)', whiteSpace: 'nowrap' }}
				>
					Group:
				</Box>
				<Box
					component="span"
					title={name}
					sx={{
						minWidth: 0,
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',
						fontWeight: 600,
						color: 'var(--ink)',
					}}
				>
					{name}
				</Box>
				{currentCount !== undefined && (
					<Box
						component="span"
						sx={{
							px: '6px',
							minWidth: 20,
							borderRadius: 'var(--r-full)',
							bgcolor: 'var(--surface-sunken)',
							color: 'var(--ink-2)',
							fontSize: 'var(--fs-small)',
							fontWeight: 600,
							lineHeight: '18px',
							display: 'inline-flex',
							alignItems: 'center',
							justifyContent: 'center',
						}}
					>
						<Count n={currentCount} />
					</Box>
				)}
				<ExpandMoreIcon
					aria-hidden
					sx={{
						ml: 'auto',
						fontSize: 20,
						color: 'var(--ink-2)',
						transition: 'transform var(--dur-fast) var(--ease)',
						transform: open ? 'rotate(180deg)' : 'none',
					}}
				/>
			</Button>

			<Popover
				open={open}
				anchorEl={anchor}
				onClose={close}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'left' }}
				slotProps={{
					paper: {
						role: 'dialog',
						'aria-label': 'Choose a file group',
						sx: {
							width: 340,
							mt: 0.5,
							border: '1px solid var(--line)',
							boxShadow: 'var(--e2)',
						},
					},
				}}
			>
				<Box
					sx={{
						p: 'var(--s2)',
						borderBottom: '1px solid var(--line)',
					}}
				>
					<TextField
						size="small"
						fullWidth
						autoFocus
						value={query}
						placeholder="Find a group"
						onChange={(event) => setQuery(event.target.value)}
						onKeyDown={(event) => {
							if (event.key === 'ArrowDown') {
								event.preventDefault();
								menuListRef.current
									?.querySelector<HTMLElement>(
										'[role=menuitem]:not([aria-disabled=true])',
									)
									?.focus();
							}
						}}
						slotProps={{
							input: {
								startAdornment: (
									<InputAdornment position="start">
										<SearchIcon fontSize="small" />
									</InputAdornment>
								),
							},
							htmlInput: { 'aria-label': 'Find a group' },
						}}
						sx={{
							'& .MuiOutlinedInput-root': {
								height: 'var(--ctl-h-sm)',
							},
						}}
					/>
				</Box>

				<MenuList
					dense
					ref={menuListRef}
					sx={{ maxHeight: 360, overflowY: 'auto', py: 'var(--s1)' }}
				>
					{item('all', 'All data files', allCount, { kind: 'all' })}
					{item('ungrouped', 'Ungrouped', ungroupedCount, {
						kind: 'ungrouped',
					})}
					<ListSubheader
						sx={{
							position: 'sticky',
							top: 0,
							lineHeight: '28px',
							fontSize: 'var(--fs-small)',
							color: 'var(--ink-3)',
							bgcolor: 'var(--surface)',
						}}
					>
						File groups
					</ListSubheader>
					{sorted.length === 0 ? (
						<MenuItem disabled sx={{ fontSize: 'var(--fs-body)' }}>
							No groups yet
						</MenuItem>
					) : matching.length === 0 ? (
						<MenuItem disabled sx={{ fontSize: 'var(--fs-body)' }}>
							No groups match "{query.trim()}"
						</MenuItem>
					) : (
						matching.map((group) => {
							const next: Selection = {
								kind: 'group',
								id: group.pkFileGroupId,
							};
							return item(
								selectionKey(next),
								group.name,
								group.fileCount,
								next,
							);
						})
					)}
				</MenuList>

				{canEdit && (
					<Box sx={{ borderTop: '1px solid var(--line)' }}>
						<Button
							fullWidth
							variant="text"
							startIcon={<AddIcon />}
							onClick={() => {
								close();
								onNewGroup();
							}}
							sx={{
								justifyContent: 'flex-start',
								color: 'var(--accent)',
								px: 'var(--s3)',
								borderRadius: 0,
							}}
						>
							New group
						</Button>
					</Box>
				)}
			</Popover>
		</>
	);
}
